import classNames from "classnames/bind";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { FreeMode, Navigation } from "swiper/modules";
import slugify from "slugify";

import Carousel from "common/blocks/Carousel";
import VehicleCardV2 from "common/blocks/VehicleCardV2";
import Button from "common/blocks/Button";

import type { ResponseVehicle } from "services/motorpoint/shortlist";
import type { ResolvedShopfront } from "resolvers/data/shopfrontResolver";

import styles from "./CarouselView.module.scss";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";

const cx = classNames.bind(styles);

type CarouselViewProps = ResolvedShopfront & {
    vehicleMakeModel: string;
    vehicles: ResponseVehicle[];
    shortlistedStocks?: number[];
    vehicleCardSettings?: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
};

const CarouselView = ({
    carouselView,
    vehicleMakeModel,
    vehicles = [],
    shortlistedStocks,
    vehicleCardSettings,
    vehicleDisplaySettings,
}: CarouselViewProps) => {
    const [slidesOffsetBefore, setSlidesOffesetBefore] = useState<number>(0);
    const [articlePositionX, setArticlePositionX] = useState<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);

    const isXS = useMediaQuery("(min-width: 0px) and (max-width: 743px)");
    const isL = useMediaQuery("(min-width: 1440px)");

    useEffect(() => {
        const onResize = () => {
            const articleDetails = document.getElementById("article-details");
            const contentPositionX = articleDetails?.getBoundingClientRect().x;
            setSlidesOffesetBefore(contentPositionX || 0);

            const articleContent = document.getElementById("article-content");
            const articleContentPositionX = articleContent?.getBoundingClientRect().x;
            setArticlePositionX(articleContentPositionX || 0);
        };

        onResize();

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const spaceBetween = isXS ? 10 : 20;

    const firstVehicle = vehicles[0];
    const shopLink = [];

    if (firstVehicle) {
        shopLink.push(
            firstVehicle.IsCommercial ? "/used-vans" : "/used-cars",
            firstVehicle.Make.toLowerCase().replaceAll(" ", "-"),
            firstVehicle.Model.toLowerCase().replaceAll(" ", "-")
        );
    }

    return (
        <div
            className={cx("carouselView")}
            style={
                {
                    "--carousel-view-height": `${contentRef.current?.clientHeight}px`,
                } as React.CSSProperties
            }
        >
            <div className={cx("reservedSpace")} />
            <div className={cx("container")}>
                <div
                    className={cx("content")}
                    ref={contentRef}
                    style={
                        {
                            marginLeft: isL || isXS ? "auto" : `${slidesOffsetBefore}px`,
                            "--slides-offset-before": `${slidesOffsetBefore}px`,
                            "--article-position-x": `${articlePositionX}px`,
                        } as React.CSSProperties
                    }
                >
                    <Carousel
                        id={`${slugify(vehicleMakeModel)}Carousel`}
                        title={`${carouselView.headingPrefixText} ${vehicleMakeModel}`}
                        className={cx("carousel")}
                        navigationClassName={cx("navigation")}
                        slidesOffsetBefore={0}
                        slidesOffsetAfter={isL ? 0 : isXS ? 10 : slidesOffsetBefore * 2}
                        spaceBetween={spaceBetween}
                        slidesPerView="auto"
                        updateOnWindowResize={true}
                        freeMode={true}
                        modules={[FreeMode, Navigation]}
                    >
                        {vehicles.map((item) => (
                            <VehicleCardV2
                                key={item.StockBookNumber}
                                vehicle={item}
                                linkTarget={carouselView.vehicleCardTarget || ""}
                                isSaved={(shortlistedStocks ?? []).includes(item.StockBookNumber)}
                                vehicleCardSettings={vehicleCardSettings}
                                vehicleDisplaySettings={vehicleDisplaySettings}
                            />
                        ))}
                    </Carousel>
                    <div className={cx("ctaButtonWrapper")}>
                        <Button
                            theme="primary"
                            href={shopLink.join("/")}
                            target={carouselView.ctaButtonTarget || ""}
                        >
                            {`${carouselView.ctaPrefixText} ${vehicleMakeModel}`}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CarouselView;
